import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNotification } from '../../contexts/CustomNotificationContext.jsx';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon, CheckIcon, XMarkIcon, ArrowPathIcon, ChevronUpIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import ConsumerProductForm from '../../components/ConsumerProductForm.jsx';
import { primaryButtonClasses } from '../../libs/sharedClassNames.js';
import { normalizeGTINForStorage, compareGTINs, toStoredConsumerEan } from '../../libs/gtinUtils.js';
import GTINInput from '../../components/GTINInput.jsx';
import UnitInput from '../../components/UnitInput.jsx';
import { 
  updateConsumerProduct,
  updateMultipleConsumerProducts,
  updateDefaultCasepack,
  insertCasepack,
  updateCasepack,
  deleteCasepackV1,
} from '../../libs/fetch.js';

const ConsumerProducts = ({ products, allProducts, onRefresh, organizationId, canEditProducts = true }) => {
  const { sendNotification } = useNotification();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [showProductLineDropdown, setShowProductLineDropdown] = useState({});
  const [productLineSearch, setProductLineSearch] = useState({});
  const [selectedOptionIndex, setSelectedOptionIndex] = useState({});
  const [focusedGtinField, setFocusedGtinField] = useState(null);
  const [focusedProductNameField, setFocusedProductNameField] = useState(null);
  const [focusedProductLineField, setFocusedProductLineField] = useState(null);
  const [gtinValidationErrors, setGtinValidationErrors] = useState({});
  const [casepackGtinValidationErrors, setCasepackGtinValidationErrors] = useState({});
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingCasepackId, setEditingCasepackId] = useState(null);
  const [casepackEditData, setCasepackEditData] = useState({});
  const [addingNewCasepack, setAddingNewCasepack] = useState(false);
  const [addingNewCasepackProductId, setAddingNewCasepackProductId] = useState(null);
  const [newCasepackData, setNewCasepackData] = useState({});
  const [sortField, setSortField] = useState('product_line');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isDefaultSort, setIsDefaultSort] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savingCasepackId, setSavingCasepackId] = useState(null);
  const [savingProductId, setSavingProductId] = useState(null);
  const dropdownRefs = useRef({});

  const handleCreateProduct = async (productData) => {
    try {
      // The form now handles the API call, so we just need to refresh the data
      setShowCreateForm(false);
      onRefresh();
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleSeasonalChange = async (productId, isSeasonal) => {
    try {
      const result = await updateConsumerProduct(productId, { is_seasonal: isSeasonal });
      if (result.error) {
        console.error('Error updating seasonal status:', result.error);
        sendNotification('Error', 'Failed to update seasonal status. Please try again.', true);
        return;
      }
      onRefresh();
    } catch (error) {
      console.error('Error updating seasonal status:', error);
      sendNotification('Error', 'Failed to update seasonal status. Please try again.', true);
    }
  };

  const handleDiscontinuedChange = async (productId, isDiscontinued) => {
    try {
      const result = await updateConsumerProduct(productId, { is_discontinued: isDiscontinued });
      if (result.error) {
        console.error('Error updating discontinued status:', result.error);
        sendNotification('Error', 'Failed to update discontinued status. Please try again.', true);
        return;
      }
      onRefresh();
    } catch (error) {
      console.error('Error updating discontinued status:', error);
      sendNotification('Error', 'Failed to update discontinued status. Please try again.', true);
    }
  };

  const toggleExpanded = (productId) => {
    if (isBulkEditing) return; // Disable expansion during bulk editing
    
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        // If we're closing a product and it's being edited, cancel the editing
        if (editingProductId === productId) {
          stopInlineEditing(productId);
        }
        // If a casepack is being edited for this product, cancel casepack editing
        if (editingCasepackId && expandedProducts.has(productId)) {
          stopCasepackInlineEditing();
        }
        // If adding a new casepack for this product, cancel that too
        if (addingNewCasepack && addingNewCasepackProductId === productId) {
          cancelAddingNewCasepack();
        }
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleAllExpanded = () => {
    if (isBulkEditing) return; // Disable expansion during bulk editing
    
    // Check if all products are expanded
    const allExpanded = products.length > 0 && products.every(p => expandedProducts.has(p.id));
    
    if (allExpanded) {
      // Collapse all - cancel any editing first
      if (editingProductId) {
        stopInlineEditing(editingProductId);
      }
      if (editingCasepackId) {
        stopCasepackInlineEditing();
      }
      if (addingNewCasepack) {
        cancelAddingNewCasepack();
      }
      setExpandedProducts(new Set());
    } else {
      // Expand all
      const allProductIds = new Set(products.map(p => p.id));
      setExpandedProducts(allProductIds);
    }
  };

  const enableBulkEditing = () => {
    // Cancel any active single-row editing
    if (editingProductId) {
      stopInlineEditing(editingProductId);
    }
    if (editingCasepackId) {
      stopCasepackInlineEditing();
    }
    if (addingNewCasepack) {
      cancelAddingNewCasepack();
    }
    
    // Clear any existing validation errors
    setGtinValidationErrors({});
    setCasepackGtinValidationErrors({});
    
    setIsBulkEditing(true);
    setExpandedProducts(new Set()); // Close any expanded rows
    // Initialize bulk edit data with current values, formatting GTINs for display
    const initialData = {};
    products.forEach(product => {
      initialData[product.id] = {
        product_name: product.product_name,
        product_line: product.product_line || '',
        ean_upc: product.ean_upc ? formatGTIN(product.ean_upc) : '',
        customer_sku: product.customer_sku || '',
      };
    });
    setBulkEditData(initialData);
    setOriginalData(initialData);
  };

  const cancelBulkEditing = () => {
    setIsBulkEditing(false);
    setBulkEditData({});
    setOriginalData({});
    setShowProductLineDropdown({});
    setProductLineSearch({});
    setSelectedOptionIndex({});
    setFocusedGtinField(null);
    setFocusedProductNameField(null);
    setFocusedProductLineField(null);
    setEditingProductId(null);
    setEditingCasepackId(null);
    setCasepackEditData({});
    setGtinValidationErrors({});
    setCasepackGtinValidationErrors({});
  };

  const saveBulkEditing = async () => {
    try {
      // Get all products that have changes
      const changedProducts = Object.keys(bulkEditData).filter(productId => 
        hasChanges(productId)
      );

      if (changedProducts.length === 0) {
        alert('No changes to save.');
        return;
      }

      // Count total validation errors
      const totalErrors = Object.keys(bulkEditData).reduce((total, productId) => {
        return total + (gtinValidationErrors[productId]?.hasError ? 1 : 0);
      }, 0);

      if (totalErrors > 0) {
        alert(`Please fix ${totalErrors} validation error${totalErrors !== 1 ? 's' : ''} before saving.`);
        return;
      }

      setIsSaving(true);

      // Prepare the data for the database
      const productsDataForDB = changedProducts.map(productId => {
        const product = bulkEditData[productId];
        return {
          id: productId,
          product_name: product.product_name,
          product_line: product.product_line,
          ean_upc: product.ean_upc ? toStoredConsumerEan(product.ean_upc) : null,
          customer_sku: product.customer_sku,
        };
      });

      // Call the actual API function
      const result = await updateMultipleConsumerProducts(productsDataForDB, organizationId);
      
      if (result.error) {
        console.error('Error updating products:', result.error);
        alert('Error updating products. Please try again.');
        return;
      }
      
      // Refresh data first, then exit bulk editing mode to avoid flash
      await onRefresh();
      
      // Exit bulk editing mode
      cancelBulkEditing();
    } catch (error) {
      console.error('Error saving bulk edits:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkEditChange = (productId, field, value) => {
    // For GTIN field, enforce 14-digit limit
    let processedValue = value;
    if (field === 'gtin' && value && value.length > 14) {
      processedValue = value.slice(0, 14);
    }
    
    setBulkEditData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: processedValue
      }
    }));
  };

  const handleGTINValidationChange = useCallback((productId, validationState) => {
    setGtinValidationErrors(prev => ({
      ...prev,
      [productId]: validationState
    }));
  }, []);



  const handleCasepackGTINValidationChange = useCallback((casepackId, validationState) => {
    setCasepackGtinValidationErrors(prev => ({
      ...prev,
      [casepackId]: validationState
    }));
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
        setIsDefaultSort(false);
      } else if (sortDirection === 'desc') {
        // Revert to default sort
        setSortField('product_line');
        setSortDirection('asc');
        setIsDefaultSort(true);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
      setIsDefaultSort(false);
    }
  };

  const getSortedProducts = () => {
    const sorted = [...products].sort((a, b) => {
      if (isDefaultSort) {
        // Default sort: Product Line first, then Product Name
        let aProductLine = a.product_line || '';
        let bProductLine = b.product_line || '';
        let aName = a.product_name || '';
        let bName = b.product_name || '';
        
        // Convert to string for comparison
        aProductLine = aProductLine.toString().toLowerCase();
        bProductLine = bProductLine.toString().toLowerCase();
        aName = aName.toString().toLowerCase();
        bName = bName.toString().toLowerCase();
        
        // First sort by Product Line
        const productLineComparison = aProductLine.localeCompare(bProductLine);
        if (productLineComparison !== 0) {
          return productLineComparison;
        }
        
        // If Product Lines are the same, sort by Product Name
        return aName.localeCompare(bName);
      } else {
        // Custom sort by selected field
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Convert to string for comparison
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
        
        if (sortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
    });
    
    return sorted;
  };

  const hasChanges = (productId) => {
    if ((!isBulkEditing && editingProductId !== productId) || !bulkEditData[productId]) return false;
    const original = originalData[productId];
    const current = bulkEditData[productId];
    
    // Handle empty values properly - treat empty strings as null
    const normalizeValue = (val) => {
      if (val === null || val === undefined || val === '') return null;
      return val.toString().trim();
    };
    
    // For GTIN, use special comparison that ignores leading zeros
    const gtinChanged = !compareGTINs(original.ean_upc, current.ean_upc);
    
    return (
      normalizeValue(original.product_name) !== normalizeValue(current.product_name) ||
      normalizeValue(original.product_line) !== normalizeValue(current.product_line) ||
      normalizeValue(original.customer_sku) !== normalizeValue(current.customer_sku) ||
      gtinChanged
    );
  };



  const hasFieldChanges = (productId, field) => {
    if ((!isBulkEditing && editingProductId !== productId) || !bulkEditData[productId]) return false;
    const original = originalData[productId];
    const current = bulkEditData[productId];
    
    // For GTIN field, use special comparison that ignores leading zeros
    if (field === 'ean_upc' || field === 'gtin') {
      return !compareGTINs(original[field], current[field]);
    }
    
    // Handle empty values properly - treat empty strings as null
    const normalizeValue = (val) => {
      if (val === null || val === undefined || val === '') return null;
      return val.toString().trim();
    };
    
    const normalizedOriginal = normalizeValue(original[field]);
    const normalizedCurrent = normalizeValue(current[field]);
    
    return normalizedOriginal !== normalizedCurrent;
  };

  const formatGTIN = (gtin) => {
    if (!gtin || gtin === '-') return '';
    
    const gtinStr = gtin.toString();
    
    // Remove leading zeros for display, but keep at least 12 digits for UPCs
    const withoutLeadingZeros = gtinStr.replace(/^0+/, '');
    
    // If it becomes less than 12 digits, it means we had too many leading zeros
    // Keep it as at least 12 digits (UPC format)
    if (withoutLeadingZeros.length < 12) {
      return gtinStr.slice(-12);
    }
    
    return withoutLeadingZeros;
  };

  const isProductNameValid = (productId) => {
    if ((!isBulkEditing && editingProductId !== productId) || !bulkEditData[productId]) return true;
    const name = bulkEditData[productId]?.product_name;
    return name !== null && name !== undefined && name.trim() !== '';
  };

  const hasAnyChanges = () => {
    return Object.keys(bulkEditData).some(productId => hasChanges(productId));
  };

  /**
   * Get total validation errors by checking each product's validation state
   */
  const getTotalValidationErrors = () => {
    let totalErrors = 0;
    
    // Check product name validation
    Object.keys(bulkEditData).forEach(productId => {
      if (!isProductNameValid(productId)) {
        totalErrors++;
      }
    });
    
    // Check GTIN validation errors from the component
    Object.keys(gtinValidationErrors).forEach(productId => {
      if (gtinValidationErrors[productId]?.hasError) {
        totalErrors++;
      }
    });
    
    // Check casepack GTIN validation errors
    Object.keys(casepackGtinValidationErrors).forEach(casepackId => {
      if (casepackGtinValidationErrors[casepackId]?.hasError) {
        totalErrors++;
      }
    });
    

    
    return totalErrors;
  };

  // Memoize the total validation errors to prevent unnecessary recalculations
  const totalValidationErrors = useMemo(() => {
    const total = getTotalValidationErrors();

    return total;
  }, [gtinValidationErrors, casepackGtinValidationErrors, bulkEditData]);

  const isCasepackNameValid = (casepackId) => {
    if (editingCasepackId !== casepackId) return true;
    return true;
  };

  const hasCasepackGTINConflict = (casepackId) => {
    if (editingCasepackId !== casepackId) return false;
    const gtin = casepackEditData[casepackId]?.gtin;
    // If no GTIN, no conflict
    if (!gtin || gtin.trim() === '') return false;
    
    // Check if there are validation errors indicating conflicts
    return casepackGtinValidationErrors[casepackId]?.hasError || false;
  };

  const getDefaultCasepackName = (casepack, productName) => {
    if (!casepack || !productName) return '';
    const units = casepack.units_per_casepack || 1;
    return `${productName} (Case of ${units})`;
  };

  const resetCasepackName = (casepackId) => {

    setCasepackEditData(prev => {
      const newData = {
        ...prev,
        [casepackId]: {
          ...prev[casepackId],
          name: null
        }
      };

      return newData;
    });
  };

  const startAddingNewCasepack = (productId) => {
    // Stop any existing editing
    if (editingCasepackId) {
      stopCasepackInlineEditing();
    }
    if (editingProductId) {
      stopInlineEditing(editingProductId);
    }
    
    setAddingNewCasepack(true);
    setAddingNewCasepackProductId(productId);
    setNewCasepackData({
      name: null,
      units_per_casepack: '',
      gtin: ''
    });
  };

  const cancelAddingNewCasepack = () => {
    setAddingNewCasepack(false);
    setAddingNewCasepackProductId(null);
    setNewCasepackData({});
  };

  const saveNewCasepack = async () => {
    try {
      // Validate the data before saving
      if (!isNewCasepackNameValid()) {
        alert('Please fix all validation errors before saving.');
        return;
      }
      
      // Validate Quantity per casepack
      const units = newCasepackData.units_per_casepack;
      if (!units || units === '' || units < 1) {
        alert('Quantity per casepack must be at least 1.');
        return;
      }

      // Prepare the data for the database
      const newCasepackDataForDB = {
        name: newCasepackData.name || null,
        gtin: newCasepackData.gtin ? normalizeGTINForStorage(newCasepackData.gtin) : null,
        consumer_products: { [addingNewCasepackProductId]: newCasepackData.units_per_casepack || 1 }
      };



      // Call the actual API function
      const result = await insertCasepack(newCasepackDataForDB, organizationId);
      
      if (result.error) {
        console.error('Error creating casepack:', result.error);
        alert('Error creating casepack. Please try again.');
        return;
      }
      
      // Refresh data first, then clear state to avoid flash
      await onRefresh();
      
      setAddingNewCasepack(false);
      setAddingNewCasepackProductId(null);
      setNewCasepackData({});
    } catch (error) {
      console.error('Error saving new casepack:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingCasepackId(null);
    }
  };

  const isNewCasepackNameValid = () => {
    // Name is always valid (can be null for default)
    return true;
  };

  const hasCasepackFieldChanges = (casepackId, field) => {
    if (editingCasepackId !== casepackId) return false;
    const casepack = products.flatMap(p => p.casepacks || []).find(c => c.id === casepackId);
    if (!casepack) return false;
    
    const original = casepack;
    const current = casepackEditData[casepackId];
    
    if (!current) return false;
    
    // For GTIN field, use special comparison that ignores leading zeros
    if (field === 'gtin') {
      const originalGtin = original.gtin ? normalizeGTINForStorage(original.gtin) : null;
      const currentGtin = normalizeGTINForStorage(current.gtin);
      return !compareGTINs(originalGtin, currentGtin);
    }
    
    // For name field, compare with both original name and default name
    if (field === 'name') {
      const originalName = original.name;
      const currentName = current.name;
      
      // If original name is null/empty, compare with default name
      if (!originalName) {
        const product = products.find(p => p.casepacks?.some(cp => cp.id === casepackId));
        const defaultName = product ? getDefaultCasepackName(original, product.product_name) : '';
        // Only show as changed if current name differs from default AND is not null/empty
        const hasChanged = currentName && currentName.trim() && currentName !== defaultName;
        return hasChanged;
      }
      
      // Otherwise compare with original name
      const hasChanged = currentName !== originalName;
      return hasChanged;
    }
    
    // Handle empty values properly - treat empty strings as null
    const normalizeValue = (val) => {
      if (val === null || val === undefined || val === '') return null;
      return val.toString().trim();
    };
    
    const normalizedOriginal = normalizeValue(original[field]);
    const normalizedCurrent = normalizeValue(current[field]);
    
    return normalizedOriginal !== normalizedCurrent;
  };

  const startInlineEditing = (productId) => {
    // Stop any casepack editing first
    if (editingCasepackId) {
      stopCasepackInlineEditing();
    }
    setEditingProductId(productId);
    // Initialize editing data with current values
    const product = products.find(p => p.id === productId);
    if (product) {
      setBulkEditData(prev => ({
        ...prev,
        [productId]: {
          product_name: product.product_name,
          product_line: product.product_line || '', // Use empty string instead of null
          ean_upc: product.ean_upc ? formatGTIN(product.ean_upc) : '',
          customer_sku: product.customer_sku || '',
        }
      }));
      setOriginalData(prev => ({
        ...prev,
        [productId]: {
          product_name: product.product_name,
          product_line: product.product_line || '', // Use empty string instead of null
          ean_upc: product.ean_upc ? formatGTIN(product.ean_upc) : '',
          customer_sku: product.customer_sku || '',
        }
      }));
    }
  };

  const stopInlineEditing = (productId) => {
    setEditingProductId(null);
    // Revert any unsaved changes
    setBulkEditData(prev => {
      const newData = { ...prev };
      delete newData[productId];
      return newData;
    });
    setOriginalData(prev => {
      const newData = { ...prev };
      delete newData[productId];
      return newData;
    });
  };

  const saveInlineEdit = async (productId) => {
    try {
      // Validate the data
      if (!isProductNameValid(productId)) {
        alert('Please fix all validation errors before saving.');
        return;
      }

      setIsSaving(true);
      setSavingProductId(productId);

      // Prepare the data for the database
      const productData = {
        product_name: bulkEditData[productId].product_name || null,
        product_line: bulkEditData[productId].product_line || null,
        ean_upc: bulkEditData[productId].ean_upc ? toStoredConsumerEan(bulkEditData[productId].ean_upc) : null,
        customer_sku: bulkEditData[productId].customer_sku || null
      };



      // Call the actual API function
      const result = await updateConsumerProduct(productId, productData);
      
      if (result.error) {
        console.error('Error updating product:', result.error);
        alert('Error updating product. Please try again.');
        return;
      }
            
      // Refresh data first, then clear editing state to avoid flash
      await onRefresh();
      
      setEditingProductId(null);
      
      // Clear the editing data
      setBulkEditData(prev => {
        const newData = { ...prev };
        delete newData[productId];
        return newData;
      });
      setOriginalData(prev => {
        const newData = { ...prev };
        delete newData[productId];
        return newData;
      });
    } catch (error) {
      console.error('Error saving inline edit:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingProductId(null);
    }
  };

  const startCasepackInlineEditing = (casepackId) => {
    // Stop any product editing first
    if (editingProductId) {
      stopInlineEditing(editingProductId);
    }
    setEditingCasepackId(casepackId);
    // Find the casepack and initialize edit data
    const casepack = products.flatMap(p => p.casepacks || []).find(c => c.id === casepackId);
    if (casepack) {
      setCasepackEditData(prev => ({
        ...prev,
        [casepackId]: {
          name: casepack.name,
          units_per_casepack: casepack.units_per_casepack,
          gtin: casepack.gtin || ''
        }
      }));
    }
  };

  const stopCasepackInlineEditing = () => {
    setEditingCasepackId(null);
    setCasepackEditData(prev => {
      const newData = { ...prev };
      delete newData[editingCasepackId];
      return newData;
    });
  };

  const saveCasepackInlineEdit = async (casepackId) => {
    try {
      // Validate the data before saving
      if (!isCasepackNameValid(casepackId) || hasCasepackGTINConflict(casepackId)) {
        alert('Please fix all validation errors before saving.');
        return;
      }
      
      // Validate units per casepack
      const units = casepackEditData[casepackId]?.units_per_casepack;
      if (!units || units === '' || units < 1) {
        alert('Quantity per casepack must be at least 1.');
        return;
      }

      setIsSaving(true);
      setSavingCasepackId(casepackId);

      // Find which product this casepack belongs to
      const productForCasepack = products.find(p => 
        p.casepacks && p.casepacks.some(c => c.id === casepackId)
      );
      if (!productForCasepack) {
        alert('Error: Could not find product for this casepack.');
        return;
      }

      // Prepare the data for the database
      const casepackData = {
        name: casepackEditData[casepackId]?.name || null,
        gtin: casepackEditData[casepackId]?.gtin ? normalizeGTINForStorage(casepackEditData[casepackId].gtin) : null,
        consumer_products: { [productForCasepack.id]: casepackEditData[casepackId]?.units_per_casepack || 1 }
      };

      // Call the actual API function
      const result = await updateCasepack(casepackId, casepackData);
      
      if (result.error) {
        console.error('Error updating casepack:', result.error);
        alert('Error updating casepack. Please try again.');
        return;
      }

      // Refresh data first, then clear editing state to avoid flash
      await onRefresh();
      
      setEditingCasepackId(null);
      setCasepackEditData(prev => {
        const newData = { ...prev };
        delete newData[casepackId];
        return newData;
      });
    } catch (error) {
      console.error('Error saving casepack inline edit:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingCasepackId(null);
    }
  };

  const setCasepackAsDefault = async (casepackId) => {
    try {
      // Find the product that contains this casepack
      const product = products.find(p => p.casepacks?.some(c => c.id === casepackId));
      if (!product) {
        console.error('Product not found for casepack:', casepackId);
        return;
      }

      setIsSaving(true);

      // Call the actual API function
      const result = await updateDefaultCasepack(product.id, casepackId);
      
      if (result.error) {
        console.error('Error setting casepack as default:', result.error);
        alert('Error setting casepack as default. Please try again.');
        return;
      }

      // Refresh data in background without showing loading state
      onRefresh();
    } catch (error) {
      console.error('Error setting casepack as default:', error);
      alert('Error setting casepack as default. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Get all existing GTINs across all product types for duplicate checking
  const getAllExistingGTINs = useMemo(() => {
    const allGtins = new Set();
    
    // Add GTINs from consumer products
    products.forEach(product => {
      if (product.ean_upc) {
        allGtins.add(normalizeGTINForStorage(product.ean_upc));
      }
      // Add GTINs from casepacks
      if (product.casepacks) {
        product.casepacks.forEach(casepack => {
          if (casepack.gtin) {
            allGtins.add(normalizeGTINForStorage(casepack.gtin));
          }
        });
      }
    });
    
    return allGtins;
  }, [products]);

  // Get unique product lines from all products (unfiltered) - use allProducts if provided, otherwise fall back to products
  const productsForProductLines = allProducts || products;
  const existingProductLines = useMemo(() => {
    const lines = new Set();
    productsForProductLines.forEach(product => {
      if (product.product_line && product.product_line.trim()) {
        lines.add(product.product_line.trim());
      }
    });
    return Array.from(lines).sort();
  }, [productsForProductLines]);

  const toggleProductLineDropdown = (productId) => {
    setShowProductLineDropdown(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
    if (!showProductLineDropdown[productId]) {
      setProductLineSearch(prev => ({
        ...prev,
        [productId]: ''
      }));
      
      // Set initial selection to current value or first option
      const currentValue = bulkEditData[productId]?.product_line !== undefined ? bulkEditData[productId].product_line : products.find(p => p.id === productId)?.product_line;
      const filteredOptions = getFilteredProductLineOptions(productId);
      
      if (filteredOptions.length > 0) {
        if (currentValue) {
          // Find the index of the current value
          const currentIndex = filteredOptions.findIndex(option => option.value === currentValue);
          setSelectedOptionIndex(prev => ({
            ...prev,
            [productId]: currentIndex >= 0 ? currentIndex : 0
          }));
        } else {
          // No current value (null), select the "none" option (index 0)
          setSelectedOptionIndex(prev => ({
            ...prev,
            [productId]: 0
          }));
        }
      }
    }
  };

  const handleProductLineSelect = (productId, value) => {
    handleBulkEditChange(productId, 'product_line', value);
    setShowProductLineDropdown(prev => ({
      ...prev,
      [productId]: false
    }));
    setProductLineSearch(prev => ({
      ...prev,
      [productId]: ''
    }));
    setSelectedOptionIndex(prev => ({
      ...prev,
      [productId]: -1
    }));
  };

  const handleAddNewProductLine = (productId, value) => {
    const searchValue = value || productLineSearch[productId] || '';
    if (searchValue.trim()) {
      handleBulkEditChange(productId, 'product_line', searchValue.trim());
      setShowProductLineDropdown(prev => ({
        ...prev,
        [productId]: false
      }));
      setProductLineSearch(prev => ({
        ...prev,
        [productId]: ''
      }));
      setSelectedOptionIndex(prev => ({
        ...prev,
        [productId]: -1
      }));
    }
  };

  const clearProductLine = (productId) => {
    // Revert to original value instead of setting to null
    const originalValue = originalData[productId]?.product_line ?? null;
    handleBulkEditChange(productId, 'product_line', originalValue);
    setShowProductLineDropdown(prev => ({
      ...prev,
      [productId]: false
    }));
    setSelectedOptionIndex(prev => ({
      ...prev,
      [productId]: -1
    }));
  };

  const handleKeyDown = (productId, e) => {
    let currentIndex = selectedOptionIndex[productId];
    const filteredOptions = getFilteredProductLineOptions(productId);
    
    // If no index is set, default to 0 (first option)
    if (currentIndex === undefined || currentIndex === null) {
      currentIndex = 0;
    }
    
    if (filteredOptions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Simple: if we can go down, go down; otherwise stay put
        if (currentIndex < filteredOptions.length - 1) {
          setSelectedOptionIndex(prev => ({ ...prev, [productId]: currentIndex + 1 }));
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        // Simple: if we can go up, go up; otherwise stay put
        if (currentIndex > 0) {
          setSelectedOptionIndex(prev => ({ ...prev, [productId]: currentIndex - 1 }));
        }
        break;
        
      case 'Enter':
        e.preventDefault();
        if (currentIndex >= 0 && currentIndex < filteredOptions.length) {
          const selectedOption = filteredOptions[currentIndex];
          if (selectedOption.isNew) {
            handleAddNewProductLine(productId, selectedOption.value);
          } else {
            handleProductLineSelect(productId, selectedOption.value);
          }
          setShowProductLineDropdown(prev => ({ ...prev, [productId]: false }));
          setProductLineSearch(prev => ({ ...prev, [productId]: '' }));
          setSelectedOptionIndex(prev => ({ ...prev, [productId]: -1 }));
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setShowProductLineDropdown(prev => ({ ...prev, [productId]: false }));
        setProductLineSearch(prev => ({ ...prev, [productId]: '' }));
        setSelectedOptionIndex(prev => ({ ...prev, [productId]: -1 }));
        break;
    }
  };

  const getFilteredProductLineOptions = (productId) => {
    const searchTerm = productLineSearch[productId] || '';
    const options = [];
    
    // Only add "none" option if there's no search term
    if (!searchTerm) {
      options.push({ value: null, isNew: false, display: '(none)' });
    }
    
    // Add existing product lines that match search
    existingProductLines.forEach(line => {
      if (line.toLowerCase().includes(searchTerm.toLowerCase())) {
        options.push({ value: line, isNew: false });
      }
    });
    
    // Add "add new" option if search has value and no exact match
    if (searchTerm && !existingProductLines.some(line => line.toLowerCase() === searchTerm.toLowerCase())) {
      options.push({ value: searchTerm, isNew: true });
    }
    
    // If no options and no search term, show all existing product lines
    if (options.length === 1 && !searchTerm) { // Only "none" option
      existingProductLines.forEach(line => {
        options.push({ value: line, isNew: false });
      });
    }
    
    return options;
  };

  const revertRowChanges = (productId) => {
    setBulkEditData(prev => ({
      ...prev,
      [productId]: {
        product_name: originalData[productId]?.product_name || '',
        product_line: originalData[productId]?.product_line || '',
        ean_upc: originalData[productId]?.ean_upc ? formatGTIN(originalData[productId].ean_upc) : '',
        customer_sku: originalData[productId]?.customer_sku || ''
      }
    }));
  };

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(showProductLineDropdown).forEach(productId => {
        if (showProductLineDropdown[productId] && dropdownRefs.current[productId] && !dropdownRefs.current[productId].contains(event.target)) {
          setShowProductLineDropdown(prev => ({
            ...prev,
            [productId]: false
          }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductLineDropdown]);

  const isCasepackUnitsValid = (casepackId) => {
    if (editingCasepackId !== casepackId) return true;
    const units = casepackEditData[casepackId]?.units_per_casepack;
    return units && units !== '' && units >= 1;
  };

  const getCasepackUnitsError = (casepackId) => {
    if (editingCasepackId !== casepackId) return null;
    const units = casepackEditData[casepackId]?.units_per_casepack;
    if (!units || units === '' || units < 1) {
      return 'Must be greater than 1';
    }
    return null;
  };

  const isNewCasepackUnitsValid = () => {
    const units = newCasepackData.units_per_casepack;
    return units && units !== '' && units >= 1;
  };

  const getNewCasepackUnitsError = () => {
    const units = newCasepackData.units_per_casepack;
    if (!units || units === '' || units < 1) {
      return 'Must be greater than 1';
    }
    return null;
  };

  const getCasepackUnitsWarning = (casepackId) => {
    if (editingCasepackId !== casepackId) return null;
    const units = casepackEditData[casepackId]?.units_per_casepack;
    const unitsNum = typeof units === 'string' ? parseInt(units) || 0 : (units || 0);
    if (unitsNum > 999) {
      return 'Large quantity detected. Please double-check this value.';
    }
    return null;
  };

  const getNewCasepackUnitsWarning = () => {
    const units = newCasepackData.units_per_casepack;
    const unitsNum = typeof units === 'string' ? parseInt(units) || 0 : (units || 0);
    if (unitsNum > 999) {
      return 'Large quantity detected. Please double-check this value.';
    }
    return null;
  };

  if (products.length === 0) {
    return (
      <div>
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No products</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first consumer product.</p>
          {canEditProducts && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className={`inline-flex items-center px-6 py-2 text-sm font-medium ${primaryButtonClasses}`}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                New Consumer Product
              </button>
            </div>
          )}
        </div>

        {/* Create Product Modal - Always render this */}
        {showCreateForm && (
          <ConsumerProductForm
            onSubmit={handleCreateProduct}
            onCancel={() => setShowCreateForm(false)}
            existingProducts={products}
            existingProductLines={existingProductLines}
            organizationId={organizationId}
          />
        )}
      </div>
    );
  }

  return (
    <div>


      {/* Table Controls */}
      <div className="flex justify-between items-center mb-4">
        {/* Tab Title */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Consumer Products</h2>
        </div>
        
        {/* Action Buttons */}
        <div>
          {!isBulkEditing ? (
            <div className="flex gap-2">
              {canEditProducts && (
                <button
                  type="button"
                  onClick={enableBulkEditing}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium ${primaryButtonClasses}`}
                >
                  Bulk Edit
                </button>
              )}
              {canEditProducts && (
                <button
                  type="button"
                  onClick={() => setShowCreateForm(true)}
                  className={`inline-flex items-center px-6 py-2 text-sm font-medium ${primaryButtonClasses}`}
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  New Consumer Product
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
                <div className="flex items-center">
                  <svg className="h-4 w-4 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-yellow-800 font-medium">Bulk Edit Mode</span>
                </div>
              </div>
              
              {totalValidationErrors > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-red-800 font-medium">
                      {`${totalValidationErrors} validation error${totalValidationErrors !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={saveBulkEditing}
                  disabled={!hasAnyChanges() || totalValidationErrors > 0}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md mr-2 ${
                    !hasAnyChanges() || totalValidationErrors > 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-primaryBrandColor border border-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow'
                  }`}
                >
                  {totalValidationErrors > 0 ? 'Fix Errors' : 'Save'}
                </button>
                
                <button
                  type="button"
                  onClick={cancelBulkEditing}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products Table */}
      <div className="mt-2 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="w-full divide-y divide-gray-300 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-12">
                      <button
                        onClick={toggleAllExpanded}
                        disabled={isBulkEditing || products.length === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                        title={products.length > 0 && products.every(p => expandedProducts.has(p.id)) ? "Collapse all" : "Expand all"}
                      >
                        {products.length > 0 && products.every(p => expandedProducts.has(p.id)) ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                    </th>
                    <th 
                      scope="col" 
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('product_name')}
                    >
                      <div className="flex items-center">
                        Name
                        {sortField === 'product_name' && !isDefaultSort && (
                          sortDirection === 'asc' ? 
                            <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                            <ChevronDownIcon className="ml-1 h-4 w-4" />
                        )}
                        {isDefaultSort && sortField === 'product_name' && (
                          <div className="ml-1 h-4 w-4 flex flex-col items-center justify-center">
                            <div className="w-1 h-1 bg-gray-400 rounded-full mb-0.5"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('product_line')}
                    >
                      <div className="flex items-center">
                        Product Line
                        {sortField === 'product_line' && (
                          isDefaultSort ? 
                            <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                            sortDirection === 'asc' ? 
                              <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                              <ChevronDownIcon className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none w-36"
                      onClick={() => handleSort('gtin')}
                    >
                      <div className="flex items-center">
                        UPC/EAN
                        {sortField === 'gtin' && !isDefaultSort && (
                          sortDirection === 'asc' ? 
                            <ChevronUpIcon className="ml-1 h-4 w-4" /> : 
                            <ChevronDownIcon className="ml-1 h-4 w-4" />
                        )}
                        {isDefaultSort && sortField === 'gtin' && (
                          <div className="ml-1 h-4 w-4 flex flex-col items-center justify-center">
                            <div className="w-1 h-1 bg-gray-400 rounded-full mb-0.5"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="pl-6 pr-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      SKU
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 w-20">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {getSortedProducts().map((product) => (
                    <React.Fragment key={product.id}>
                                            <tr className="hover:bg-gray-50">
                        <td className="py-2 pl-4 pr-1 text-sm text-gray-500 w-12">
                          <div className="flex items-center h-8">
                            {!isBulkEditing ? (
                              <button
                                onClick={() => toggleExpanded(product.id)}
                                className="text-gray-400 hover:text-gray-600 flex items-center"
                              >
                                {product.casepacks && product.casepacks.length > 0 ? (
                                  <span className="text-xs">
                                    ({product.casepacks.length})
                                  </span>
                                ) : (
                                  <span 
                                    className="text-amber-500 hover:text-amber-600"
                                    title="Missing Casepacks"
                                  >
                                    ⚠️
                                  </span>
                                )}
                                {expandedProducts.has(product.id) ? (
                                  <ChevronDownIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4" />
                                )}
                              </button>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </div>
                        </td>
                                                <td className="px-3 py-4 text-sm font-medium text-gray-900">
                          <div className="relative flex items-center h-8 w-full">
                            {(isBulkEditing || editingProductId === product.id) ? (
                              <>
                                <input
                                  type="text"
                                  value={bulkEditData[product.id]?.product_name !== undefined ? bulkEditData[product.id].product_name : product.product_name}
                                  onChange={(e) => handleBulkEditChange(product.id, 'product_name', e.target.value)}
                                  onFocus={() => setFocusedProductNameField(product.id)}
                                  onBlur={() => setFocusedProductNameField(null)}
                                  className={`w-full px-0 py-0 text-sm border border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400 focus:outline-none text-gray-900 h-8 ${
                                    !isProductNameValid(product.id) ? 'bg-red-50 border-red-300' :
                                    hasFieldChanges(product.id, 'product_name') ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'
                                  }`}
                                />
                                {!isProductNameValid(product.id) && focusedProductNameField === product.id && (
                                  <div className="absolute z-50 top-full mt-1 left-0 w-48 bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 font-normal shadow-lg">
                                    <div className="font-medium mb-1">Required Field</div>
                                    <div>Product name cannot be empty.</div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="flex items-center truncate">
                                <span className="truncate">{product.product_name}</span>
                                {!product.is_active && (
                                  <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 flex-shrink-0">
                                    Inactive
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                                                                         <td className="px-3 py-4 text-sm text-gray-500">
                          {(isBulkEditing || editingProductId === product.id) ? (
                            <div className="relative flex items-center h-8 w-full" ref={(el) => dropdownRefs.current[product.id] = el}>
                              <button
                                type="button"
                                onClick={() => toggleProductLineDropdown(product.id)}
                                onFocus={() => setFocusedProductLineField(product.id)}
                                onBlur={() => setFocusedProductLineField(null)}
                                className={`w-full px-0 py-0 text-sm border border-gray-200 text-left focus:border-green-400 focus:ring-1 focus:ring-green-400 focus:outline-none text-gray-900 h-8 ${
                                  hasFieldChanges(product.id, 'product_line') ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'
                                }`}
                              >
                                <span className="block truncate">
                                  {bulkEditData[product.id]?.product_line !== undefined ? (bulkEditData[product.id].product_line || '(none)') : (product.product_line || '(none)')}
                                </span>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    clearProductLine(product.id);
                                  }}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full cursor-pointer"
                                  title="Clear product line"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </div>
                              </button>
                               
                               {showProductLineDropdown[product.id] && (
                                 <div className="absolute z-10 top-full mt-1 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 text-sm max-h-60 overflow-y-auto">
                                   {/* Search input */}
                                   <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2">
                                     <input
                                       type="text"
                                       value={productLineSearch[product.id] || ''}
                                       onChange={(e) => setProductLineSearch(prev => ({
                                         ...prev,
                                         [product.id]: e.target.value
                                       }))}
                                       onKeyDown={(e) => handleKeyDown(product.id, e)}
                                       placeholder="Search or add..."
                                       className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                     />
                                   </div>
                                   
                                   {/* Filtered existing product lines */}
                                   {getFilteredProductLineOptions(product.id).map((option, index) => {
                                     const isSelected = index === selectedOptionIndex[product.id];
                                     return (
                                       <button
                                         key={option.value}
                                         type="button"
                                         onClick={() => {
                                           if (option.isNew) {
                                             handleAddNewProductLine(product.id, option.value);
                                           } else {
                                             handleProductLineSelect(product.id, option.value);
                                           }
                                           setShowProductLineDropdown(prev => ({ ...prev, [product.id]: false }));
                                           setProductLineSearch(prev => ({ ...prev, [product.id]: '' }));
                                           setSelectedOptionIndex(prev => ({ ...prev, [product.id]: -1 }));
                                         }}
                                         className={`w-full px-3 py-2 text-left text-gray-900 block ${
                                           isSelected
                                             ? 'bg-green-100 text-green-900' 
                                             : 'hover:bg-red-50'
                                         } ${option.isNew ? 'border-t border-gray-200 bg-green-50 hover:bg-green-100' : ''}`}
                                       >
                                         {option.isNew ? (
                                           <span className="text-green-700">+ Add "{option.value}"</span>
                                         ) : (
                                           option.display || option.value
                                         )}
                                       </button>
                                     );
                                   })}
                                   

                                   
                                   {/* No results message */}
                                   {productLineSearch[product.id] && getFilteredProductLineOptions(product.id).length === 0 && (
                                     <div className="px-3 py-2 text-gray-500 text-sm">
                                       No matching product lines found
                                     </div>
                                   )}
                                 </div>
                               )}
                             </div>
                           ) : (
                             <div className="relative flex items-center h-8 w-full" ref={(el) => dropdownRefs.current[product.id] = el}>
                               <button
                                 type="button"
                                 disabled={true}
                                 className="w-full px-0 py-0 text-sm text-left text-gray-900 h-8 cursor-default truncate"
                                 title="Product line"
                               >
                                 <span className="block truncate">
                                   {product.product_line || '(none)'}
                                 </span>
                               </button>
                             </div>
                           )}
                         </td>
                                                                         <td className="px-3 py-4 text-sm text-gray-500 w-36">
                          <div className="flex items-center h-8 w-full">
                            {(isBulkEditing || editingProductId === product.id) ? (
                              <GTINInput
                                value={bulkEditData[product.id]?.ean_upc !== undefined ? (bulkEditData[product.id].ean_upc || '') : (product.ean_upc || '')}
                                onChange={(value) => handleBulkEditChange(product.id, 'ean_upc', value)}
                                onFocus={() => setFocusedGtinField(product.id)}
                                onBlur={() => setFocusedGtinField(null)}
                                placeholder="12-digit UPC or 13-digit EAN"
                                className={`w-full ${gtinValidationErrors[product.id]?.hasError ? 'bg-red-50' : hasFieldChanges(product.id, 'ean_upc') ? 'bg-yellow-50' : 'bg-gray-50'}`}
                                excludeProductId={product.id}
                                existingProducts={products}
                                isBulkEditing={isBulkEditing}
                                bulkEditData={bulkEditData}
                                originalGtin={product.ean_upc}
                                errorPosition="left"
                                maxWidth={256}
                                maxLength={13}
                                borderless={true}
                                textAlign="left"
                                onValidationChange={(validationState) => handleGTINValidationChange(product.id, validationState)}
                              />
                            ) : (
                              <span className="truncate">{formatGTIN(product.ean_upc)}</span>
                            )}
                          </div>
                        </td>

                        <td className="pl-6 pr-3 py-4 text-sm text-gray-500">
                          <div className="flex items-center h-8 w-full">
                            {(isBulkEditing || editingProductId === product.id) ? (
                              <input
                                type="text"
                                value={bulkEditData[product.id]?.customer_sku ?? product.customer_sku ?? ''}
                                onChange={(e) => handleBulkEditChange(product.id, 'customer_sku', e.target.value)}
                                placeholder="Enter SKU"
                                className={`w-full px-0 py-0 text-sm border border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400 focus:outline-none text-gray-900 h-8 ${
                                  hasFieldChanges(product.id, 'customer_sku') ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'
                                }`}
                              />
                            ) : (
                              <span className="truncate">{product.customer_sku}</span>
                            )}
                          </div>
                        </td>

                                                                         <td className="relative py-4 pl-3 pr-4 text-right text-sm font-medium w-20">
                          <div className="flex items-center justify-end h-8 w-full">
                            {!isBulkEditing && editingProductId === null && editingCasepackId === null && !addingNewCasepack ? (
                              <>
                                {canEditProducts && (
                                  <button 
                                    onClick={() => startInlineEditing(product.id)}
                                    disabled={isSaving}
                                    className={`mr-4 text-sm ${
                                      isSaving
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-primaryBrandColor hover:text-primaryBrandColor/80'
                                    }`}
                                  >
                                    Edit
                                  </button>
                                )}
                              </>
                            ) : editingProductId === product.id ? (
                              <>
                                <button 
                                  onClick={() => saveInlineEdit(product.id)}
                                  disabled={isSaving && savingProductId === product.id || (!hasFieldChanges(product.id, 'product_name') && !hasFieldChanges(product.id, 'product_line') && !hasFieldChanges(product.id, 'ean_upc') && !hasFieldChanges(product.id, 'customer_sku')) || (gtinValidationErrors[product.id]?.hasError)}
                                  className={`inline-flex items-center justify-center p-1 text-xs font-medium rounded-md mr-2 ${
                                    isSaving && savingProductId === product.id || (!hasFieldChanges(product.id, 'product_name') && !hasFieldChanges(product.id, 'product_line') && !hasFieldChanges(product.id, 'ean_upc') && !hasFieldChanges(product.id, 'customer_sku')) || (gtinValidationErrors[product.id]?.hasError)
                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                      : 'text-primaryBrandColor border border-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow'
                                  }`}
                                  title="Save changes"
                                >
                                  {isSaving && savingProductId === product.id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                                  ) : (
                                    <CheckIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <button 
                                  onClick={() => stopInlineEditing(product.id)}
                                  disabled={isSaving && savingProductId === product.id}
                                  className={`inline-flex items-center justify-center p-1 text-xs font-medium rounded-md ${
                                    isSaving && savingProductId === product.id
                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                      : 'text-white bg-red-600 hover:bg-red-700 border border-transparent'
                                  } shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                                  title="Cancel editing"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center justify-end">
                                {hasChanges(product.id) && (
                                  <button
                                    type="button"
                                    onClick={() => revertRowChanges(product.id)}
                                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                                    title="Revert changes for this row"
                                  >
                                    <ArrowPathIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                                             {/* Expanded Casepacks Row */}
                                              {!isBulkEditing && expandedProducts.has(product.id) && (
                         <tr>
                           <td colSpan="6" className="bg-gray-50 px-6 py-4">
                            <div className="flex gap-6">
                              {/* Product Flags Section - Left Side */}
                              <div className="w-40 flex-shrink-0">
                                <div className="text-sm text-gray-900 font-medium mb-3">Product Flags</div>
                                <div className="flex flex-col gap-3">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={product.is_seasonal || false}
                                      onChange={(e) => handleSeasonalChange(product.id, e.target.checked)}
                                      className="h-4 w-4 rounded border-gray-300 text-primaryBrandColor focus:ring-primaryBrandColor cursor-pointer"
                                    />
                                    <span className="text-sm text-gray-700">Seasonal</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={product.is_discontinued || false}
                                      onChange={(e) => handleDiscontinuedChange(product.id, e.target.checked)}
                                      className="h-4 w-4 rounded border-gray-300 text-primaryBrandColor focus:ring-primaryBrandColor cursor-pointer"
                                    />
                                    <span className="text-sm text-gray-700">Discontinued</span>
                                  </label>
                                </div>
                              </div>

                              {/* Casepacks Section - Right Side */}
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="text-sm text-gray-900 font-medium">Casepacks</div>
                                  {canEditProducts && (
                                    <button 
                                      onClick={() => startAddingNewCasepack(product.id)}
                                      disabled={addingNewCasepack || editingCasepackId || editingProductId}
                                      className="text-sm text-indigo-600 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                      + Add New Casepack
                                    </button>
                                  )}
                                </div>
                            {(product.casepacks && product.casepacks.length > 0) || (addingNewCasepack && addingNewCasepackProductId === product.id) ? (
                              <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                        {/* Empty header for toggle space */}
                                      </th>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Casepack Name
                                      </th>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                        Quantity
                                      </th>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        GTIN/UPC
                                      </th>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                        {/* Actions */}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {/* Existing casepacks */}
                                    {product.casepacks && product.casepacks.map((casepack) => (
                                      <tr key={casepack.id} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm text-gray-500 w-16">
                                          {casepack.is_default && (
                                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                              Default
                                            </span>
                                          )}
                                          {!casepack.is_default && editingProductId === null && editingCasepackId === null && !addingNewCasepack && (
                                            <button
                                              onClick={() => setCasepackAsDefault(casepack.id)}
                                              disabled={isSaving}
                                              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium transition-colors ${
                                                isSaving
                                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                                  : 'bg-gray-50 text-gray-400 hover:bg-blue-100 hover:text-blue-800 cursor-pointer'
                                              }`}
                                              title={isSaving ? "Saving..." : "Set as default casepack"}
                                            >
                                              Default
                                            </button>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                          {editingCasepackId === casepack.id ? (
                                            <div className="relative flex items-center h-8">
                                              <input
                                                type="text"
                                                value={(() => {
                                                  const editName = casepackEditData[casepack.id]?.name;
                                                  if (editName !== undefined) {
                                                    return editName === null ? '' : editName;
                                                  }
                                                  return casepack.name || '';
                                                })()}
                                                onChange={(e) => setCasepackEditData(prev => ({
                                                  ...prev,
                                                  [casepack.id]: {
                                                    ...prev[casepack.id],
                                                    name: e.target.value === '' ? null : e.target.value
                                                  }
                                                }))}
                                                placeholder={getDefaultCasepackName(casepack, product.product_name)}
                                                className={`w-full pr-8 px-0 py-0 text-sm border border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400 focus:outline-none text-gray-900 h-8 ${
                                                  hasCasepackFieldChanges(casepack.id, 'name') ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'
                                                }`}
                                              />
                                              <button
                                                type="button"
                                                onClick={() => resetCasepackName(casepack.id)}
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                                                title="Clear field to use default name"
                                              >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                              </button>
                                            </div>
                                          ) : (
                                            <div className="flex items-center h-8">
                                              <span>{casepack.name || getDefaultCasepackName(casepack, product.product_name)}</span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500 w-48">
                                          {editingCasepackId === casepack.id ? (
                                            <div className="relative flex items-center h-8">
                                              <UnitInput
                                                value={(() => {
                                                  const editUnits = casepackEditData[casepack.id]?.units_per_casepack;
                                                  if (editUnits !== undefined) {
                                                    return editUnits;
                                                  }
                                                  return casepack.units_per_casepack;
                                                })()}
                                                onChange={(value) => setCasepackEditData(prev => ({
                                                  ...prev,
                                                  [casepack.id]: {
                                                    ...prev[casepack.id],
                                                    units_per_casepack: value
                                                  }
                                                }))}
                                                min={1}
                                                className={`w-full ${
                                                  hasCasepackFieldChanges(casepack.id, 'units_per_casepack') ? 'bg-yellow-50 border-yellow-300' : ''
                                                }`}
                                              />
                                              {getCasepackUnitsError(casepack.id) && (
                                                <div className="absolute z-50 bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 shadow-lg top-full mt-1 left-0">
                                                  <div className="font-medium mb-1">Validation Error</div>
                                                  <div className="break-words whitespace-normal">
                                                    {getCasepackUnitsError(casepack.id)}
                                                  </div>
                                                </div>
                                              )}
                                              {!getCasepackUnitsError(casepack.id) && getCasepackUnitsWarning(casepack.id) && (
                                                <div className="ml-2 relative group">
                                                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                                                    {getCasepackUnitsWarning(casepack.id)}
                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="flex items-center h-8">
                                              <span>{casepack.units_per_casepack} units</span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500 w-40">
                                          {editingCasepackId === casepack.id ? (
                                            <div className="relative flex items-center h-8">
                                              <GTINInput
                                                value={(() => {
                                                  const editGtin = casepackEditData[casepack.id]?.gtin;
                                                  if (editGtin !== undefined) {
                                                    return editGtin === null ? '' : editGtin;
                                                  }
                                                  return casepack.gtin || '';
                                                })()}
                                                onChange={(value) => setCasepackEditData(prev => ({
                                                  ...prev,
                                                  [casepack.id]: {
                                                    ...prev[casepack.id],
                                                    gtin: value === '' ? null : value
                                                  }
                                                }))}
                                                placeholder="Enter GTIN or UPC"
                                                className="w-full bg-gray-50"
                                                excludeProductId={product.id}
                                                excludeCasepackId={casepack.id}
                                                existingProducts={products}
                                                originalGtin={casepack.gtin}
                                                errorPosition="bottom"
                                                maxWidth={192}
                                                maxLength={14}
                                                borderless={true}
                                                textAlign="left"
                                                onValidationChange={(validationState) => handleCasepackGTINValidationChange(casepack.id, validationState)}
                                              />
                                            </div>
                                          ) : (
                                            <div className="flex items-center h-8 w-full overflow-hidden">
                                              <span>{casepack.gtin ? formatGTIN(casepack.gtin) : ''}</span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500 w-32 min-w-[128px] max-w-[128px] overflow-hidden">
                                          <div className="flex items-center justify-end h-8 w-full">
                                            {editingCasepackId === casepack.id ? (
                                              <div className="flex space-x-2">
                                                <button 
                                                  onClick={() => saveCasepackInlineEdit(casepack.id)}
                                                  disabled={isSaving && savingCasepackId === casepack.id || !hasCasepackFieldChanges(casepack.id, 'name') && !hasCasepackFieldChanges(casepack.id, 'units_per_casepack') && !hasCasepackFieldChanges(casepack.id, 'gtin') || (casepackGtinValidationErrors[casepack.id]?.hasError) || !isCasepackUnitsValid(casepack.id)}
                                                  className={`inline-flex items-center justify-center p-1 text-xs font-medium rounded-md ${
                                                    isSaving && savingCasepackId === casepack.id || !hasCasepackFieldChanges(casepack.id, 'name') && !hasCasepackFieldChanges(casepack.id, 'units_per_casepack') && !hasCasepackFieldChanges(casepack.id, 'gtin') || (casepackGtinValidationErrors[casepack.id]?.hasError) || !isCasepackUnitsValid(casepack.id)
                                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                      : 'text-primaryBrandColor border border-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow'
                                                  }`}
                                                  title="Save changes"
                                                >
                                                  {isSaving && savingCasepackId === casepack.id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                                                  ) : (
                                                    <CheckIcon className="h-4 w-4" />
                                                  )}
                                                </button>
                                                <button 
                                                  onClick={() => stopCasepackInlineEditing()}
                                                  disabled={isSaving && savingCasepackId === casepack.id}
                                                  className={`inline-flex items-center rounded-md p-1 text-xs font-medium ${
                                                    isSaving && savingCasepackId === casepack.id
                                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                      : 'text-white bg-red-600 hover:bg-red-700 border border-transparent'
                                                  } shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                                                  title="Cancel editing"
                                                >
                                                  <XMarkIcon className="h-4 w-4" />
                                                </button>
                                              </div>
                                            ) : editingProductId === null && editingCasepackId === null && !addingNewCasepack ? (
                                              <div className="flex space-x-2">
                                                {canEditProducts && (
                                                  <button 
                                                    onClick={() => startCasepackInlineEditing(casepack.id)}
                                                    disabled={isSaving}
                                                    className={`text-sm ${
                                                      isSaving
                                                        ? 'text-gray-400 cursor-not-allowed'
                                                        : 'text-primaryBrandColor hover:text-primaryBrandColor/80'
                                                    }`}
                                                  >
                                                    Edit
                                                  </button>
                                                )}
                                                {canEditProducts && !casepack.is_default && (
                                                  <button 
                                                    onClick={async () => {
                                                      if (!confirm('Are you sure you want to delete this casepack?')) {
                                                        return;
                                                      }
                                                      const result = await deleteCasepackV1(casepack.id);
                                                      if (result !== false) {
                                                        onRefresh();
                                                      } else {
                                                        sendNotification('Error', 'Failed to delete casepack. Please try again or contact support.', true);
                                                      }
                                                    }}
                                                    className="text-red-600 hover:text-red-900 text-sm"
                                                  >
                                                    Delete
                                                  </button>
                                                )}
                                              </div>
                                            ) : (
                                              // Hide buttons when any row is being edited
                                              <div className="h-8"></div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                    
                                    {/* New Casepack Row */}
                                    {addingNewCasepack && addingNewCasepackProductId === product.id && (
                                      <tr className="hover:bg-gray-50 bg-blue-50">
                                        <td className="px-3 py-2 text-sm text-gray-500 w-16">
                                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                            New
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                          <div className="relative flex items-center h-8">
                                            <input
                                              type="text"
                                              value={newCasepackData.name || ''}
                                              onChange={(e) => setNewCasepackData(prev => ({
                                                ...prev,
                                                name: e.target.value === '' ? null : e.target.value
                                              }))}
                                              placeholder={getDefaultCasepackName({ units_per_casepack: newCasepackData.units_per_casepack || '' }, product.product_name)}
                                              className="w-full pr-8 px-0 py-0 text-sm border border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400 focus:outline-none text-gray-900 h-8 bg-gray-50"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => setNewCasepackData(prev => ({ ...prev, name: null }))}
                                              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                                              title="Clear field to use default name"
                                            >
                                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            </button>
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500 w-48">
                                          <div className="relative flex items-center h-8">
                                            <UnitInput
                                              value={newCasepackData.units_per_casepack || ''}
                                              onChange={(value) => setNewCasepackData(prev => ({
                                                ...prev,
                                                units_per_casepack: value
                                              }))}
                                              min={1}
                                              className="w-full"
                                            />
                                            {getNewCasepackUnitsError() && (
                                              <div className="absolute z-50 bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 shadow-lg top-full mt-1 left-0">
                                                <div className="font-medium mb-1">Validation Error</div>
                                                <div className="break-words whitespace-normal">
                                                  {getNewCasepackUnitsError()}
                                                </div>
                                              </div>
                                            )}
                                            {!getNewCasepackUnitsError() && getNewCasepackUnitsWarning() && (
                                              <div className="ml-2 relative group">
                                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                                                  {getNewCasepackUnitsWarning()}
                                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500 w-40">
                                          <div className="flex items-start w-full">
                                            <GTINInput
                                              value={newCasepackData.gtin || ''}
                                              onChange={(value) => setNewCasepackData(prev => ({
                                                ...prev,
                                                gtin: value
                                              }))}
                                              placeholder="Enter GTIN or UPC"
                                              className="w-full bg-gray-50"
                                              existingProducts={products}
                                              errorPosition="bottom"
                                              maxWidth={192}
                                              maxLength={14}
                                              borderless={true}
                                              textAlign="left"
                                              onValidationChange={(validationState) => {
                                                // For new casepack, we'll use a temporary ID
                                                const tempId = 'new-casepack';
                                                setCasepackGtinValidationErrors(prev => ({
                                                  ...prev,
                                                  [tempId]: validationState
                                                }));
                                              }}
                                            />
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500 w-32 min-w-[128px] max-w-[128px] overflow-hidden">
                                          <div className="flex justify-end space-x-2 w-full">
                                            <button 
                                              onClick={saveNewCasepack}
                                              disabled={casepackGtinValidationErrors['new-casepack']?.hasError || !isNewCasepackUnitsValid()}
                                              className={`inline-flex items-center justify-center p-1 text-xs font-medium rounded-md ${
                                                casepackGtinValidationErrors['new-casepack']?.hasError || !isNewCasepackUnitsValid()
                                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                  : 'text-primaryBrandColor border border-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow'
                                              }`}
                                              title="Save new casepack"
                                            >
                                              <CheckIcon className="h-4 w-4" />
                                            </button>
                                            <button 
                                              onClick={cancelAddingNewCasepack}
                                              className="inline-flex items-center justify-center p-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                              title="Cancel adding casepack"
                                            >
                                              <XMarkIcon className="h-4 w-4" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0">
                                    <span className="text-amber-500 text-lg">⚠️</span>
                                  </div>
                                  <div className="ml-3">
                                    <h3 className="text-sm font-medium text-amber-800">
                                      Missing Casepack Configuration
                                    </h3>
                                    <div className="mt-1 text-sm text-amber-700">
                                      This product is missing casepack configurations. As a result, unit conversions and calculations may be inaccurate.
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create Product Modal */}
              {showCreateForm && (
          <ConsumerProductForm
            onSubmit={handleCreateProduct}
            onCancel={() => setShowCreateForm(false)}
            existingProducts={products}
            existingProductLines={existingProductLines}
            organizationId={organizationId}
          />
        )}
    </div>
  );
};

export default ConsumerProducts;
